/**
 * heldOffline — pure, side-effect-free classifier for the specific
 * "realtime is paused" backend signal.
 *
 * A mint attempt is treated as HELD if and only if the backend
 * response is HTTP 503 AND its parsed JSON body carries the exact
 * FastAPI-shaped envelope with the nested marker:
 *
 *     { "detail": { "state": "REALTIME_HELD_OFFLINE_GATE_ACTIVE", ... } }
 *
 * The api.js mintClientSecret helper preserves the ENTIRE parsed body
 * verbatim in `err.detail`, so the classifier walks the nested path
 * `err.detail.detail.state`. This match is by parsed structured fields
 * only — never by substring on stringified payloads.
 *
 * FLAT-BODY REJECTION (positive negative-case coverage):
 *
 *   A top-level flat body {"state": "REALTIME_HELD_OFFLINE_GATE_ACTIVE"}
 *   without the FastAPI envelope MUST NOT classify as held. Real
 *   production held responses always emit the envelope; anything else
 *   is either a lookalike or an unknown 503 and stays a real error.
 *
 * Any other non-2xx (401, 400, 500, non-503, malformed JSON body, plain
 * text body, network error, correct envelope but wrong status, correct
 * status but wrong nested state) remains an ordinary error.
 *
 * This module intentionally has no React, no fetch, no side effects,
 * and no imports — so it can be exercised directly by the actual-module
 * Node test harness in exactly the shape the hook consumes it.
 */

export const HELD_STATE_ID = "REALTIME_HELD_OFFLINE_GATE_ACTIVE";

// Copy so the UI cannot leak env-var names, secrets, or governance
// jargon. These strings are the ONLY user-facing text for this state.
export const HELD_PRESENTATION = Object.freeze({
    headline: "Live conversations are paused",
    supporting: "Conversation testing is temporarily disabled.",
});

/**
 * Classify a thrown error from mintClientSecret (which sets
 * `err.status` and `err.detail` — the latter is the ENTIRE parsed
 * JSON body verbatim; see /app/frontend/src/lib/api.js).
 *
 * @param {*} err
 * @returns {{kind: "held", state: string, headline: string, supporting: string}
 *          | {kind: "error"}}
 */
export function classifyMintError(err) {
    if (!err || typeof err !== "object") return { kind: "error" };
    if (err.status !== 503) return { kind: "error" };
    const body = err.detail;
    // Reject non-object bodies — a string / null / number / array
    // detail can never carry the nested FastAPI envelope.
    if (
        !body
        || typeof body !== "object"
        || Array.isArray(body)
    ) {
        return { kind: "error" };
    }
    // Reject flat bodies: the nested `detail` key must itself be a
    // plain object (the FastAPI envelope). A top-level {state: HELD}
    // without the envelope is a lookalike and MUST fall through to
    // an ordinary error.
    const nested = body.detail;
    if (
        !nested
        || typeof nested !== "object"
        || Array.isArray(nested)
    ) {
        return { kind: "error" };
    }
    if (nested.state !== HELD_STATE_ID) return { kind: "error" };
    return {
        kind: "held",
        state: HELD_STATE_ID,
        headline: HELD_PRESENTATION.headline,
        supporting: HELD_PRESENTATION.supporting,
    };
}
